import React, { useState } from "react";
import {
  Grid,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import { SectionHeader } from "../../../components/ui/SectionHeader";
import idIcon from "../../../assets/svgIcons/id.svg";
import locationIcon from "../../../assets/svgIcons/live-view.svg";
import { RootState } from "../../../services/redux/store";
import {
  isFirstReferenceComplete,
} from "../../../utils/additionalDetailsValidation";

// ─── Field name helpers ──────────────────────────────────────────────────────
// Basic:   basicReference{N}<Suffix>    e.g. basicReference1FirstName
// Address: addressReference{N}<Suffix>  e.g. addressReference1AddressLine1

const refBasicField = (n: number | string, suffix: string) =>
  `basicReference${n}${suffix}`;
const refAddressField = (n: number | string, suffix: string) =>
  `addressReference${n}${suffix}`;

const BASIC_SUFFIXES = [
  "FirstName",
  "LastName",
  "EmailID",
  "Mobile",
];

const ADDRESS_SUFFIXES = [
  "AddressLine1",
  "AddressLine2",
  "Locality",
  "Landmark",
  "Area",
  "Pincode",
  "City",
  "State",
  "Country",
];

interface ReferenceDetailsProps {
  fields: any[];
  renderField: (field: any) => React.ReactNode;
  formikRef: any;
  values?: any;
  horizontalSteps?: any[];
  activeReferenceNumber?: number;
  onActiveReferenceChange?: (referenceNumber: number) => void;
}

const ReferenceDetails: React.FC<ReferenceDetailsProps> = ({
  fields,
  renderField,
  formikRef,
  values,
  horizontalSteps = [],
  activeReferenceNumber,
  onActiveReferenceChange,
}) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const fetchUserApplicationData = useSelector(
    (state: RootState) => state.stepper.fetchUserApplicationData
  );

  // Detect how many reference groups exist from fields
  const refNums = React.useMemo(() => {
    const nums = new Set<number>();
    fields.forEach((f: any) => {
      const m = String(f?.name || "").match(/^basicReference(\d+)/);
      if (m) nums.add(Number(m[1]));
    });
    return Array.from(nums).sort((a, b) => a - b);
  }, [fields]);

  const displayedRefNums = React.useMemo(() => {
    const defaults = [1, 2];
    const merged = Array.from(new Set([...defaults, ...refNums])).sort((a, b) => a - b);
    return merged.slice(0, 2);
  }, [refNums]);

  const [expanded, setExpanded] = useState<number | false>(
    activeReferenceNumber ?? displayedRefNums[0] ?? 1,
  );

  React.useEffect(() => {
    if (typeof activeReferenceNumber === "number") {
      setExpanded(activeReferenceNumber);
    }
  }, [activeReferenceNumber]);

  const getFieldsForRef = (n: number, type: "basic" | "address") => {
    const prefix = type === "basic" ? `basicReference${n}` : `addressReference${n}`;
    return fields.filter((f: any) =>
      String(f?.name || "").startsWith(prefix),
    );
  };

  const isFilled = (value: any): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "number") return true;
    if (typeof value === "boolean") return true;
    if (value && typeof value === "object") {
      if ("value" in value) return String(value.value ?? "").trim() !== "";
      return Object.keys(value).length > 0;
    }
    return String(value ?? "").trim() !== "";
  };

  const getCompletion = (sectionFields: any[]) => {
    const formValues = values || formikRef?.current?.values || {};
    const required = sectionFields.filter(
      (f: any) =>
        f?.required !== false &&
        !/^addressReference\d+(AddressLine2|Locality|Area)$/i.test(String(f?.name || "")),
    );
    const filled = required.filter((f: any) => isFilled(formValues?.[f?.name] ?? f?.value)).length;
    return `${filled} / ${required.length}`;
  };

  const enrichReferenceField = (field: any) =>
    /^addressReference\d+(City|State|Country)$/i.test(String(field?.name || ""))
      ? { ...field, disabled: true }
      : field;

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      {/* Reference accordions */}
      {displayedRefNums.map((n) => {
        const basicFields = getFieldsForRef(n, "basic");
        const addressFields = getFieldsForRef(n, "address");
        const formValues = values || formikRef?.current?.values || {};
        const isSecondReferenceDisabled =
          n === 2 &&
          !isFirstReferenceComplete(
            fields,
            formValues,
            fetchUserApplicationData?.horizontalSteps || []
          );
          console.log(isFirstReferenceComplete, 'isFirstReferenceComplete')

        return (
          <Accordion
            key={n}
            expanded={!isSecondReferenceDisabled && expanded === n}
            disabled={isSecondReferenceDisabled}
            onChange={() => {
              if (isSecondReferenceDisabled) return;
              const nextExpanded = expanded === n ? false : n;
              setExpanded(nextExpanded);
              if (nextExpanded !== false) {
                onActiveReferenceChange?.(nextExpanded);
              }
            }}
            sx={{
              mb: 2,
              border: "1px solid #d6eef1",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              backgroundColor: "#fff",
              overflow: "hidden",
              "&:before": { display: "none" },
              "&.Mui-expanded": { margin: "0 0 16px" },
              "&.Mui-disabled": {
                backgroundColor: "#fff",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: "64px",
                px: { xs: 2, sm: 3 },
                backgroundColor: "#eaf7f9",
                borderBottom: expanded === n ? "1px solid #d6eef1" : "none",
                "& .MuiAccordionSummary-content": {
                  my: 1.5,
                },
              }}
            >
              <Typography fontWeight={600} fontSize="15px">
                Reference {n}
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
              {/* Basic Details */}
              <Box sx={{ mb: 2 }}>
                <SectionHeader
                  icon={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: "8px",
                        bgcolor: "#e5feffff",
                      }}
                    >
                      <img src={idIcon} alt="icon" style={{ width: 22, height: 22 }} />
                    </Box>
                  }
                  title="Basic Details"
                  subtitle=""
                  completion={getCompletion(basicFields)}
                />
              </Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {basicFields.map((field: any) => (
                  <React.Fragment key={field?.id || field?.name}>
                    {renderField(field)}
                  </React.Fragment>
                ))}
              </Grid>

              {/* Address Details */}
              <Box sx={{ mb: 2 }}>
                <SectionHeader
                  icon={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: "8px",
                        bgcolor: "#e5feffff",
                      }}
                    >
                      <img src={locationIcon} alt="icon" style={{ width: 22, height: 22 }} />
                    </Box>
                  }
                  title="Address Details"
                  subtitle=""
                  completion={getCompletion(addressFields)}
                />
              </Box>
              <Grid container spacing={2}>
                {addressFields.map((field: any) => (
                  <React.Fragment key={field?.id || field?.name}>
                    {renderField(enrichReferenceField(field))}
                  </React.Fragment>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Fallback if no reference fields loaded yet */}
      {displayedRefNums.length === 0 && (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          No reference fields available.
        </Typography>
      )}
    </Box>
  );
};

export default ReferenceDetails;
